import type { EaseTypeImpl, Lerpable } from '../types';
import { catmullRomWeightedDual, cubicBezier, lerp, sampleToCubicBezier, sqr } from '@use-gpu/core';
import { clone, for2, for4, zip2, zip4 } from './zip';

export const EaseNumber: EaseTypeImpl<Lerpable> = {
  spline: (a: Lerpable, b: Lerpable, knots?: Lerpable[]) => {
    if (knots) {
      const [k1, k2] = knots;
      if (!k1 || !k2) throw new Error("Spline ease needs 2 knots");
      return (target: Lerpable, t: number) =>
        zip4(target, a, k1, k2, b,
          (a: number, k1: number, k2: number, b: number) => cubicBezier(t, a, k1, k2, b)
        );
    }
    else {
      return (target: Lerpable, t: number) =>
        zip2(target, a, b,
          (a: number, b: number) => lerp(a, b, t)
        );
    }
  },
  auto: (a: Lerpable, b: Lerpable, c: Lerpable, d: Lerpable) => {
    const l1 = EaseNumber.measure(a, b);
    const l2 = EaseNumber.measure(b, c);
    const l3 = EaseNumber.measure(c, d);

    const k1 = clone(b);
    const k2 = clone(b);

    const k1s: number[] = [];
    const k2s: number[] = [];

    for4(a, b, c, d,
      (a: number, b: number, c: number, d: number) => {
        const [,k1,k2] = sampleToCubicBezier((t: number) => catmullRomWeightedDual(t, a, b, c, d, l1, l2, l3))
        k1s.push(k1);
        k2s.push(k2);
      }
    );

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    zip2(k1, k1, k1, () => k1s.shift()!);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    zip2(k2, k2, k2, () => k2s.shift()!);

    return [k1, k2];
  },
  lerp: (target: Lerpable, a: Lerpable, b: Lerpable, t: number) => {
    return zip2(target, a, b, (a: number, b: number) => lerp(a, b, t));
  },
  measure: (a: Lerpable, b: Lerpable) => {
    let d = 0;
    for2(a, b, (a: number, b: number) => { d += sqr(a - b); });
    return Math.sqrt(d);
  },
};
